import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import { Button, Input, Modal, Spin } from 'antd'
import { debounce, isEmpty } from 'lodash'

import { CustomMessage } from './AlertToast'
import MarkdownRenderer from './MarkdownRender'
import { PATH_CUSTOMER_ID, IS_IFRAMES, IS_GUIDELINE_GPT } from './constants'
import './MessageHeader.css'
import { Message } from './MessageList'
import { useAnonymousId } from './useAnonymousId'
import useSyncUrlPathID from './hooks/useSyncUrlPathID'
import { useTrackEvent } from './analytics/useTrackEvent'
import { filterTitle } from './ChatHistory'

// Arrow icon component for the New button
const ArrowIcon = () => (
    <svg
        width="6.72"
        height="11.31"
        viewBox="0 0 7 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M6 1L1 6L6 11"
            stroke="#325CEB"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

function MessageHeader({
    chatTitle,
    threadID,
    fromLanding,
    setShowHistory,
    showHistory,
    onNewChat,
    isMobile,
    setSignType,
    setMobileAIChat,
    setIsReceivingAnswer,
    onAbortAndCountUsage,
    isFreddieMacStandalone = false,
    isFannieMaeStandalone = false,
    hideBackButton = false,
}) {
    const anonymousId = useAnonymousId()
    const { trackStandaloneSigninClick, trackStandaloneSignupClick } = useTrackEvent()
    const [showShareModal, setShowShareModal] = useState(false)
    const [loadingShare, setLoadingShare] = useState(false)
    const [loadingSent, setLoadingSent] = useState(false)
    const [chatMessages, setChatMessages] = useState([])
    const [emailToShare, setEmailToShare] = useState('')
    const [hasHistory, setHasHistory] = useState(false) // Track if chat history exists
    const [emailError, setEmailError] = useState('') // Email validation error message
    const debounceTimerRef = useRef(null) // Reference for debounce timer

    const dispatch = useDispatch()
    const { onRoutePathReplace } = useSyncUrlPathID()

    // Email validation function
    const validateEmail = useCallback((email) => {
        if (!email) {
            return 'This field is required.'
        }
        if (email.length > 254) {
            return 'Email address is too long.'
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(email)) {
            return 'Please enter a valid email address.'
        }
        return ''
    }, [])

    // Debounced email validation function
    const debouncedValidateEmail = useCallback(
        debounce((email) => {
            const error = validateEmail(email)
            setEmailError(error)
        }, 1000), // 1s debounce delay
        [validateEmail]
    )

    // Handle email input change with real-time validation
    const handleEmailChange = useCallback(
        (e) => {
            const value = e.target.value
            setEmailToShare(value)

            // Clear previous error immediately when user starts typing
            if (emailError) {
                setEmailError('')
            }

            // Cancel previous debounce timer
            if (debounceTimerRef.current) {
                debounceTimerRef.current.cancel()
            }

            // Only validate if there's input
            if (value.trim()) {
                debounceTimerRef.current = debouncedValidateEmail
                debouncedValidateEmail(value)
            }
        },
        [emailError, debouncedValidateEmail]
    )

    // Handle email input blur - immediate validation
    const handleEmailBlur = useCallback(() => {
        if (emailToShare.trim()) {
            // Cancel debounce and validate immediately on blur
            if (debounceTimerRef.current) {
                debounceTimerRef.current.cancel()
            }
            const error = validateEmail(emailToShare)
            setEmailError(error)
        }
    }, [emailToShare, validateEmail])

    // Cleanup debounce on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                debounceTimerRef.current.cancel()
            }
        }
    }, [])

    // Fetch chat history from API
    const fetchChatHistory = useCallback(async () => {
        const token = sessionStorage.getItem('ZeitroA')
        const body = { ThreadID: (PATH_CUSTOMER_ID === "greenbox" || PATH_CUSTOMER_ID === "newwave") ? "" : threadID }

        const response = await fetch('/los/mortgagegpt/sharechat', {
            method: 'POST',
            body: JSON.stringify(body),
            headers: {
                Authorization: 'Bearer ' + token,
                Cache: 'no-cache',
            },
        })

        if (response.status !== 200) {
            // throw new Error(`Request failed. Status code: ${response.status}`)
            return []
        }

        const json = await response.json()
        return json.chat_history
    }, [threadID])

    // Check chat history when component mounts or threadID changes
    useEffect(() => {
        if (threadID) {
            fetchChatHistory().then((history) => {
                if (history) {
                    setHasHistory(history.length > 0)
                }
            })
        }
    }, [threadID, fetchChatHistory])

    // Process chat history to remove invalid reference links
    const processChatHistory = useCallback((history) => {
        if (!history) {
            return []
        }

        return history.map((msg) => {
            if (msg.role === 'user') {
                return {
                    text: msg.content,
                    sender: 'user',
                }
            }

            // Remove invalid reference links
            const answer = msg.content.replace(
                /\[([1-9]\d*(?:\s*,\s*[1-9]\d*)*)\]/g,
                (match, numbers) => {
                    // Only filter refs if we have valid locations
                    const hasValidLocations =
                        Array.isArray(msg.locations) && msg.locations.length > 0
                    if (hasValidLocations) {
                        const refs = numbers
                            .split(',')
                            .map((n) => parseInt(n.trim()))
                        // Get unique page numbers from quoteLocations
                        const uniquePages = new Set(
                            msg.locations.map(([page]) => page)
                        )
                        const maxValidRef = uniquePages.size
                        const validRefs = refs.filter(
                            (ref) => ref <= maxValidRef
                        )
                        return validRefs.length > 0 ? match : ''
                    }

                    // If no valid locations, keep original reference
                    return match
                }
            )

            return {
                text: <MarkdownRenderer answer={answer} isGenerating={false} />,
                sender: 'bot',
            }
        })
    }, [])

    const handleShare = useCallback(async () => {
        setLoadingShare(true)

        try {
            // If history exists, get the formatted data
            const history = await fetchChatHistory()
            const processedHistory = processChatHistory(history)
            setChatMessages(processedHistory)
            setLoadingShare(false)
            setShowShareModal(true)
        } catch (err) {
            setLoadingShare(false)
            console.error('Fetch Error:', err)
            alert('Something went wrong, please try again later.')
        }
    }, [fetchChatHistory, processChatHistory])

    const copyLinkToClipboard = useCallback(async () => {
        try {
            const url =
                window.location.origin + '/mortgagegpt/sharechat/' + threadID
            await navigator.clipboard.writeText(url)
            CustomMessage.success('Link copied to clipboard')
        } catch (err) {
            console.error('Copy link to clipboard failed: ', err)
        }
    }, [threadID])

    const sendShare = useCallback(async () => {
        // Validate email before sending
        const error = validateEmail(emailToShare)
        if (error) {
            setEmailError(error)
            return
        }

        setLoadingSent(true)

        try {
            const token = sessionStorage.getItem('ZeitroA')
            const body = { Email: emailToShare, ThreadID: threadID }

            const response = await fetch('/los/mortgagegpt/sendshareemail', {
                method: 'POST',
                body: JSON.stringify(body),
                headers: {
                    Authorization: 'Bearer ' + token,
                    Cache: 'no-cache',
                },
            })

            if (response.status !== 200) {
                console.error(
                    'Looks like there was a problem. Status Code: ' +
                        response.status
                )
                setLoadingSent(false)
                return
            }

            setLoadingSent(false)
            setShowShareModal(false)
            setEmailToShare('') // Reset email after successful submission
            setEmailError('')
            alert('Email sent successfully')
        } catch (err) {
            setLoadingSent(false)
            console.error('Fetch Error :-S', err)
        }
    }, [emailToShare, threadID, validateEmail])

    // Handle modal close
    const handleModalClose = useCallback(() => {
        // Cancel any pending debounce validation
        if (debounceTimerRef.current) {
            debounceTimerRef.current.cancel()
        }
        setShowShareModal(false)
        setEmailToShare('')
        setEmailError('')
    }, [])

    useEffect(() => {
        return ()=>{
            if(sessionStorage.getItem('activeGPTmode') === 'scenario'){
                dispatch({ type: 'SHOW_PDF', data: true })
                dispatch({
                    type: 'CHANGE_PDF',
                    data: undefined,
                })
                // onNewChat()
                setShowHistory(false)
            }
        }
    },[])

    if (isMobile) {
        if (fromLanding) {
            return (
                <div
                    className="chat-message-header"
                    style={{ justifyContent: 'space-between' }}
                >
                    {!hideBackButton && (
                        <Button
                            className="back-button"
                            onClick={() => {
                                // Abort current request and count usage if applicable
                                // if (onAbortAndCountUsage) {
                                //     onAbortAndCountUsage();
                                // }
                                
                                onRoutePathReplace();
                                dispatch({ type: 'SHOW_PDF', data: true })
                                dispatch({
                                    type: 'CHANGE_PDF',
                                    data: undefined,
                                })
                                onNewChat()
                                setShowHistory(fromLanding ? false : true)
                                setIsReceivingAnswer(false);
                            }}
                            type="outline"
                        >
                            <div className="button-icon">
                                <ArrowIcon />
                            </div>
                            Back
                        </Button>
                    )}
                    {!isFreddieMacStandalone && !isFannieMaeStandalone && !IS_IFRAMES && !sessionStorage.getItem('ZeitroA') && (
                        <div className="floating-bar-buttons">
                            <button
                                className="floating-bar-btn floating-bar-btn-signup"
                                onClick={() => {
                                    trackStandaloneSignupClick();
                                    setSignType('SIGNUP')
                                }}
                            >
                                Sign up
                            </button>
                            <button
                                className="floating-bar-btn floating-bar-btn-signin"
                                onClick={() => {
                                    trackStandaloneSigninClick();
                                    setSignType('SIGNIN')
                                }}
                            >
                                Sign In
                            </button>
                        </div>
                    )}
                </div>
            )
        } else {
            return (
                <div className="chat-message-header">
                    {/* Header left container - Back button */}
                    {!hideBackButton && (
                        <div className="header-left-container">
                            <Button
                                className="back-button"
                                onClick={() => {
                                    onRoutePathReplace();
                                    dispatch({ type: 'SHOW_PDF', data: true })
                                    dispatch({
                                        type: 'CHANGE_PDF',
                                        data: undefined,
                                    })
                                    onNewChat()
                                    setMobileAIChat(true)
                                    setIsReceivingAnswer(false);
                                }}
                                type="outline"
                            >
                                <div className="button-icon">
                                    <ArrowIcon />
                                </div>
                                Back
                            </Button>
                        </div>
                    )}

                    {/* Header center container - Chat title */}
                    <div className="header-center-container">
                        <div className="chat-title">{filterTitle(chatTitle) || 'Chat'}</div>
                    </div>

                    {/* Header right container - History button only */}
                    <div className="header-right-container">
                        <img
                            src="/images/chat-history.svg"
                            alt="Chat history"
                            onClick={() => setShowHistory(!showHistory)}
                        />
                    </div>
                </div>
            )
        }
    }

    if (fromLanding) {
        return (
            <div
                className="chat-message-header"
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    height: '60px',
                    paddingLeft: '17px',
                    paddingRight: '23px',
                }}
            >
                {!hideBackButton && (
                    <Button
                        className="back-button"
                        onClick={() => {
                            // Abort current request and count usage if applicable
                            // if (onAbortAndCountUsage) {
                            //     onAbortAndCountUsage();
                            // }
                            
                            onRoutePathReplace();
                            dispatch({ type: 'SHOW_PDF', data: true })
                            dispatch({
                                type: 'CHANGE_PDF',
                                data: undefined,
                            })
                            onNewChat()
                            setShowHistory(fromLanding ? false : true)
                            setIsReceivingAnswer(false);
                        }}
                        type="outline"
                    >
                        <div className="button-icon">
                            <ArrowIcon />
                        </div>
                        Back
                    </Button>
                )}

                {(IS_IFRAMES || IS_GUIDELINE_GPT) && <div className="header-center-container">
                    <div className="chat-title">{filterTitle(chatTitle) || 'Chat'}</div>
                </div>}
            
                {!isFreddieMacStandalone && !isFannieMaeStandalone && !(IS_IFRAMES || IS_GUIDELINE_GPT) && (
                    <div className="floating-bar-buttons">
                        <button
                            className="floating-bar-btn floating-bar-btn-signup"
                            onClick={() => {
                                trackStandaloneSignupClick();
                                setSignType('SIGNUP')

                            }}
                        >
                            Sign up
                        </button>
                        <button
                            className="floating-bar-btn floating-bar-btn-signin"
                            onClick={() => {
                                trackStandaloneSigninClick();
                                setSignType('SIGNIN')
                            }}
                        >
                            Sign In
                        </button>
                    </div>
                )}
            </div>
        )
    } else {
        return (
            <div className="chat-message-header">
                {/* Header left container - Back button */}
                {!hideBackButton && (
                    <div className="header-left-container">
                        <Button
                            className="back-button"
                            onClick={() => {
                                // sessionStorage.removeItem('gpt_type');
                                // sessionStorage.removeItem('gpt_thread');
                                onRoutePathReplace();
                                dispatch({ type: 'SHOW_PDF', data: true })
                                dispatch({
                                    type: 'CHANGE_PDF',
                                    data: undefined,
                                })
                                onNewChat()
                                setShowHistory(fromLanding ? false : true)
                                setIsReceivingAnswer(false);
                            }}
                            type="outline"
                        >
                            <div className="button-icon">
                                <ArrowIcon />
                            </div>
                            Back
                        </Button>
                    </div>
                )}

                {/* Header center container - Chat title */}
                <div className="header-center-container">
                    <div className="chat-title">{filterTitle(chatTitle) || 'Chat'}</div>
                </div>

                {/* Header right container - Share and history buttons */}
                    <div className="header-right-container">
                        {loadingShare ? (
                            <Spin />
                        ) : (
                            <img
                                style={{
                                    cursor: hasHistory
                                        ? 'pointer'
                                        : 'not-allowed',
                                    opacity: hasHistory ? 1 : 0.5,
                                }}
                                src="/images/share.svg"
                                alt="Share"
                                onClick={() => hasHistory && handleShare()}
                            />
                        )}
                        {!showHistory && (
                            <div
                                style={{
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setShowHistory(!showHistory)
                                }}
                            >
                                <img style={{width: '40px', height: '40px'}} src="/images/close.svg"></img>
                            </div>
                        )}
                    </div>

                <Modal
                    className="share-chat-modal"
                    title={
                        <div className="share-chat-modal-title">Share chat</div>
                    }
                    open={showShareModal}
                    onCancel={handleModalClose}
                    footer={
                        <>
                            <a
                                className="copy-link-button"
                                onClick={() => copyLinkToClipboard()}
                            >
                                <img
                                    src="/images/copy-link.svg"
                                    style={{
                                        width: '20px',
                                        height: '20px',
                                    }}
                                />{' '}
                                Copy link
                            </a>
                            <Button
                                type="primary"
                                disabled={isEmpty(emailToShare) || !!emailError}
                                loading={loadingSent}
                                onClick={() => sendShare()}
                            >
                                Submit
                            </Button>
                        </>
                    }
                    styles={{
                        mask: { backgroundColor: 'rgba(0, 0, 0, 0.75)' },
                    }}
                >
                    <div className="share-chat-content">
                        <div className="section-title">Email address</div>
                        <Input
                            placeholder="Enter email address"
                            type="email"
                            autoComplete="off"
                            maxLength={254}
                            value={emailToShare}
                            onChange={handleEmailChange}
                            onBlur={handleEmailBlur}
                            status={emailError ? 'error' : ''}
                            style={{ marginBottom: emailError ? '0' : '32px' }}
                        />
                        {emailError && (
                            <div
                                style={{
                                    color: '#ff4d4f',
                                    fontSize: '14px',
                                    lineHeight: '1.4',
                                    marginBottom: '32px',
                                }}
                            >
                                {emailError}
                            </div>
                        )}
                        <div className="section-title">Preview</div>
                        <div className="messages-preview">
                            {chatMessages.map(({ text, sender }, index) => (
                                <Message
                                    key={index}
                                    text={text}
                                    sender={sender}
                                />
                            ))}
                        </div>
                    </div>
                </Modal>
            </div>
        )
    }
}

export default MessageHeader
